import { useState, useEffect } from 'react';

const API_URL = 'https://money-app-worker.kingpinzs.workers.dev/api';

let accessToken = null; // Access token stored in memory

export const setAccessToken = (token) => {
  accessToken = token;
};

export const getAccessToken = () => accessToken;

export async function login(email, password) {
  try {
    const response = await fetch(`${API_URL}/users/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      return { success: false, error: error.error || 'Login failed' };
    }

    const data = await response.json();
    setAccessToken(data.accessToken);

    return { success: true, accessToken: data.accessToken };
  } catch (error) {
    console.error('Login error:', error);
    return { success: false, error: error.message };
  }
}

export async function register(username, email, password) {
  const response = await fetch(`${API_URL}/users/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, email, password })
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.error || 'Registration failed');

  return data;
}

export async function refreshAccessToken() {
  const response = await fetch(`${API_URL}/refresh-token`, {
    method: 'POST',
    credentials: 'include',
  });

  if (!response.ok) {
    setAccessToken(null);
    return null;
  }

  const data = await response.json();
  setAccessToken(data.accessToken);
  return data.accessToken;
}

export async function logout() {
  await fetch(`${API_URL}/users/logout`, {
    method: 'POST',
    credentials: 'include',
  });

  setAccessToken(null);
}

export async function authenticatedRequest(endpoint, method = 'GET', data = null, token) {
  const authToken = token || getAccessToken();
  const headers = { 'Content-Type': 'application/json' };

  if (!authToken) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) throw new Error('Unauthorized');
    headers['Authorization'] = `Bearer ${refreshed}`;
  } else {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const csrfToken = document.cookie.split('; ').find(row => row.startsWith('csrfToken='))?.split('=')[1];
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;

  const options = {
    method,
    headers,
  };
  if (data) options.body = JSON.stringify(data);

  const response = await fetch(`${API_URL}${endpoint}`, options);
  if (response.status === 401 && !token) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) throw new Error('Unauthorized');
    return authenticatedRequest(endpoint, method, data, refreshed);
  }

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Request failed');
  }

  return response.json();
}

export async function apiRequest(endpoint, options = {}) {
  if (!accessToken) {
    accessToken = await refreshAccessToken();
    if (!accessToken) throw new Error('Unauthorized');
  }

  const csrfToken = document.cookie.split('; ').find(row => row.startsWith('csrfToken='))?.split('=')[1];

  options.headers = {
    ...options.headers,
    'Authorization': `Bearer ${accessToken}`,
    'X-CSRF-Token': csrfToken,
  };

  const response = await fetch(`${API_URL}${endpoint}`, options);

  if (response.status === 401) {
    accessToken = await refreshAccessToken();
    if (!accessToken) throw new Error('Unauthorized');
    return apiRequest(endpoint, options);
  }

  return response.json();
}

export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    refreshAccessToken().then(token => {
      setIsAuthenticated(!!token);
    });
  }, []);

  return { isAuthenticated, login, logout, register };
}
